import { useRef, useCallback, useMemo, useState } from 'react';
import {
  Button,
  AnimatedPopover,
  useOnClickOutside,
  useEventListener,
} from '@kinesis/bungle';
import { DisclosureDefinition } from '@/types';
import { NewDisclosure } from '@/components/new-disclosure';

// The single "New disclosure" create entry point (ported from suit's
// `NewDisclosurePopover`): a dropdown button that opens a searchable type
// picker. Replaces the old button-per-type — there is ONE button regardless of
// how many disclosure types exist; the type is chosen in the popover.
const NewDisclosurePopover = (props: NewDisclosurePopoverProps) => {
  const { onCreate, availableTypes } = props;
  const ref = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const toggle = useCallback(() => setIsOpen((current) => !current), []);
  const close = useCallback(() => setIsOpen(false), []);

  useOnClickOutside(
    useMemo(() => [ref, popoverRef], []),
    close,
  );

  useEventListener('keydown', (event: KeyboardEvent) => {
    if (event.key === 'Escape') close();
  });

  return (
    <>
      <Button ref={ref} dropdown variant='primary' onClick={toggle}>
        New disclosure
      </Button>
      <AnimatedPopover
        stopPropagation
        justify='end'
        open={isOpen}
        offset={4}
        placement='bottom'
        preventOverflow
        targetRef={ref}
      >
        <NewDisclosure
          ref={popoverRef}
          onCreate={onCreate}
          onClose={close}
          availableTypes={availableTypes}
        />
      </AnimatedPopover>
    </>
  );
};

export type NewDisclosurePopoverProps = {
  onCreate?: (v: DisclosureDefinition) => void;
  availableTypes?: string[];
};

export { NewDisclosurePopover };
